import { Info } from "@phosphor-icons/react";
import { ParentSize } from "@visx/responsive";
import clsx from "clsx";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import {
  formatShortDate,
  formatTime,
  timeSince,
  typedObjectKeys,
} from "common/helpers/utils";
import type { Metrics } from "common/types";

import Chart from "./Chart";

import NoDataImage from "assets/images/empty-placeholders/empty-graph.png";

interface StatisticsGraphProps {
  metrics: (Metrics & { timestamp: number })[];
  metricsSum: Metrics;
  selectedMetric: keyof Metrics;
  requestError: unknown;
  refetchMetrics: () => void;
  isLoading: boolean;
  isHourly: boolean;
}

export default function StatisticsGraph({
  metrics,
  metricsSum,
  selectedMetric,
  requestError,
  isLoading,
  refetchMetrics,
  isHourly,
}: StatisticsGraphProps) {
  const isEmpty = typedObjectKeys(metricsSum).every(
    (metricKey) => !metricsSum[metricKey],
  );

  const dateNow = new Date();
  dateNow.setMinutes(0);
  const lastHourUnixMs = dateNow.getTime();

  return (
    <div className="flex flex-col gap-y-2.5 flex-1">
      <div
        className={clsx(
          "ml-auto flex items-center text-black-500 text-sm-medium gap-x-1",
          (requestError || isLoading) && "invisible",
        )}
      >
        Data was synced {timeSince(lastHourUnixMs)}
        <Tooltip>
          <TooltipTrigger>
            <Info size={16} weight="fill" className="text-black-300" />
          </TooltipTrigger>
          <TooltipContent>
            Analytics data is updated every hour, on the hour. Last updated on:{" "}
            {formatShortDate(lastHourUnixMs)}, {formatTime(lastHourUnixMs)}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="relative flex h-105 lg:h-120 shrink-0">
        {requestError ? (
          <ErrorPlaceholder
            onRetry={refetchMetrics}
            errorMessage="Could not display graph data"
          />
        ) : (
          <>
            <ParentSize>
              {({ width, height }) => (
                <Chart
                  metrics={metrics}
                  selectedMetric={selectedMetric}
                  width={width}
                  height={height}
                  isEmpty={isEmpty}
                  isHourly={isHourly}
                />
              )}
            </ParentSize>
            <RenderIf condition={!isLoading && isEmpty}>
              <div className="absolute inset-0 flex flex-col pb-12 px-5">
                <EmptyPlaceholder
                  title="No data to display"
                  subtitle="The selected timeframe does not have any data to show"
                  imageSrc={NoDataImage}
                />
              </div>
            </RenderIf>
          </>
        )}
      </div>
    </div>
  );
}
